import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// Components
import BaseContentContainer from '../../components/base-content-container/base-content-container';
import ButtonsBar from '../../components/buttons-bar/buttons-bar';
import SubmitButton from '../../components/submit-button/submit-button';
import Loading from '../../components/loading/loading';
import ErrorComponent from '../../components/error/error';

// Containers
import { AnnouncementFormInputs, AnnouncementRecipients, useAnnouncementForm } from './announcement-form';

// Helpers
import {
  getAnnouncementData,
  createSetModalContent,
  buildAnnouncementRequest,
} from '../../helpers/announcement-helper';

// Models
import {
  BaseContentContainerActionButtonPosition,
  BaseContentContainerBiggerSide,
  BaseContentContainerFormat,
} from '../../models/base-content-container';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import ClassroomService from '../../services/classroom-service';
import AnnouncementService from '../../services/announcement-service';
import SchoolService from '../../services/school-service';
import StorageService from '../../services/storage-service';
import FileStorageService from '../../services/file-storage-service';

const AnnouncementCreate = () => {
  const navigate = useNavigate();
  const { setModalContent, setToastContent } = useContext(GeneralContext);
  const [error, setError] = useState(false);

  const form = useAnnouncementForm(() => getAnnouncementData());

  const [classroomList, setClassroomList] = useState<
    ClassroomStudents[] | null
  >(null);
  const [announcementService, setAnnouncementService] = useState<AnnouncementService | null>(null);
  const [fileStorageService, setFileStorageService] = useState<FileStorageService | null>(null);
  useEffect(() => {
    const classroomService = new ClassroomService();
    const schoolService = new SchoolService();
    const storageService = new StorageService();
    const schoolId = storageService.getEmployee().schoolId;

    Promise.all([
      classroomService.listClassroomStudents(),
      schoolService.getSchoolById(schoolId),
    ])
      .then(([classrooms, school]) => {
        setClassroomList(classrooms);
        setAnnouncementService(new AnnouncementService(school.uuid));
        setFileStorageService(new FileStorageService(school.uuid));
      })
      .catch(() => setError(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = (scheduleDate: string | null) =>
    form
      .submitOnce(async (current) => {
        await announcementService.createAnnouncement(
          buildAnnouncementRequest({ ...current, scheduleDate }, classroomList)
        );
        setToastContent({
          isOpen: true,
          text: scheduleDate ? 'Comunicado agendado!' : 'Comunicado enviado!',
          type: 'success',
        });
        navigate('/comunicados/listar');
      })
      .catch(() => {
        setToastContent({
          isOpen: true,
          text: 'Houve um erro ao criar o comunicado.',
          type: 'error',
        });
      });

  if (error) return <ErrorComponent type='contentFull' />;

  if (!classroomList || !announcementService || !fileStorageService)
    return <Loading type='contentFull' />;

  return (
    <BaseContentContainer
      biggerSide={BaseContentContainerBiggerSide.right}
      format={BaseContentContainerFormat.doubleWithActionButtons}
      actionButtonPosition={BaseContentContainerActionButtonPosition.bottom}
    >
      <AnnouncementFormInputs form={form} fileStorageService={fileStorageService} />
      <AnnouncementRecipients form={form} classroomList={classroomList} />
      <ButtonsBar pattern='firstSeparate' verticalAlign='bottom'>
        <Link to='/comunicados/listar'>
          <SubmitButton value='cancelar' variant='terciary' />
        </Link>
        <SubmitButton
          value='agendar envio'
          variant='secondary'
          onClick={() =>
            createSetModalContent(
              form.data,
              (submitData) => save(submitData.scheduleDate),
              form.setData,
              setModalContent
            )
          }
          disabled={!form.allowSubmit}
        />
        <SubmitButton
          value='enviar'
          variant='primary'
          onClick={() => save(null)}
          disabled={!form.allowSubmit}
        />
      </ButtonsBar>
    </BaseContentContainer>
  );
};

export default AnnouncementCreate;
